import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar, Typography, useMediaQuery } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import RunningBanner from './RunningBanner';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';
import { drawerWidth } from 'constant';
// import { changeDrawerType, openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';
import { useGetUserQuery } from 'store/services/auth';

// assets
import { IconChevronRight } from '@tabler/icons';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, drawerType }) => ({
    ...theme.typography.mainContent,
    backgroundColor: theme.palette.background.light,

    [theme.breakpoints.down('md')]: {
        marginLeft: 0,
        width: '100%'
    },
    [theme.breakpoints.up('md')]: {
        ...(drawerType === 'permanent' && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.shorter
            })
        }),

        ...(drawerType === 'temporary' && {
            marginLeft: 72,
            width: `calc(100% - ${72}px)`,
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.shorter
            })
        })
    }
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const location = useLocation();
    const [version, setVersion] = useState(null);
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const { drawerType, drawerOpen } = useSelector((state) => state.menu);
    const { container, layout } = useConfig();
    const { user } = useAuth();

    // Get lockout warning messages from the cached user dashboard response
    const { data: userData } = useGetUserQuery({ client_id: user?.client_id }, { skip: !user?.client_id });
    const rawMessages = userData?.lockout_warning_messages || userData?.fields?.lockout_warning_messages;

    const bannerMessages = Array.isArray(rawMessages) ? rawMessages.filter(Boolean) : [rawMessages].filter(Boolean);

    // useEffect(() => {
    //     dispatch(changeDrawerType('temporary'));
    //     dispatch(openDrawer(false));
    // }, [location.pathname]);

    const header = useMemo(
        () => (
            <Toolbar sx={{ p: '16px' }}>
                <Header />
            </Toolbar>
        ),
        [layout, matchDownMd]
    );

    useEffect(() => {
        fetch('/meta.json')
            .then((response) => response.json())
            .then((data) => {
                setVersion(data.version);
            })
            .catch((error) => {
                console.error('Failed to load version info:', error);
            });
    }, []);

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                enableColorOnDark
                position="fixed"
                color="background"
                elevation={0}
                sx={{
                    zIndex: theme.zIndex.drawer - 1,
                    backgroundColor: '#261A48',
                    transition: drawerOpen ? theme.transitions.create('width') : 'none'
                }}
            >
                {header}
            </AppBar>

            {/* drawer */}
            <Sidebar />

            {/* main content */}
            <Main
                open={drawerOpen}
                layout={layout}
                drawerType={drawerType}
                style={{
                    minHeight: 'unset',
                    marginBottom: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: bannerMessages.length > 0 ? 0 : '20px',
                    paddingBottom: '1px'
                }}
            >
                {/* Running Banner - sticky inside main content area */}
                {bannerMessages.length > 0 && (
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            mb: 1,
                            flexShrink: 0,
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}
                    >
                        <RunningBanner messages={bannerMessages} />
                    </Box>
                )}
                <Container
                    maxWidth={container ? 'lg' : false}
                    {...(!container && { sx: { px: { xs: 0 } } })}
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        px: !container ? { xs: 0 } : undefined
                    }}
                >
                    <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                    <Outlet />
                </Container>
                {version && (
                    <Typography
                        sx={{
                            textAlign: 'end',
                            color: 'gray',
                            paddingTop: '2px'
                        }}
                    >
                        Build V.{version || 'loading...'}
                    </Typography>
                )}
            </Main>
        </Box>
    );
};

export default MainLayout;
